import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Minus, Plus, Check, ShieldCheck, Truck, Leaf, BookOpen, Stethoscope } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { fetchProduct } from "../lib/api";
import { formatINR, packCopy } from "../lib/constants";
import { useCart } from "../context/CartContext";
import TubeArt from "../components/TubeArt";
import ProductGallery from "../components/ProductGallery";
import { ButtonRing, useBusyAction } from "../components/LoadingRing";
import { usePageSeo } from "../lib/usePageSeo";
import { crumbsJsonLd, productJsonLd } from "../lib/seoHead";
import TrustStrip from "../components/TrustStrip";
import { useSeoSettings } from "../lib/useSeoSettings";
import { useFormulation } from "../components/formulation/useFormulation";
import ComboSection from "../components/formulation/ComboSection";
import ActivesSection from "../components/formulation/ActivesSection";
import FormulationExplorer from "../components/formulation/FormulationExplorer";
import UsageSteps from "../components/formulation/UsageSteps";
import WarningSection from "../components/formulation/WarningSection";
import { NotifyMe } from "../components/NotifyMe";
import { freeDownloadUrl } from "../components/ShopCards";
import Testimonials from "../components/Testimonials";
import ProductReviews, { useProductReviews } from "../components/ProductReviews";
import { RatingLine } from "../components/Rating";
import { PreviewSections } from "../site/PreviewSections";
import ProductScienceSummary from "../components/ProductScienceSummary";
import { useProductPreview, PEdit, PreviewCtx, postEdit, PREVIEW_SLUG } from "../lib/productPreview";
import { offPreviewRequest, offReason } from "../lib/offPreview";
import { OffPreviewBanner } from "../components/OffPreviewBanner";
import { toast } from "sonner";

const DEFAULT_BLOCKS = [{ key: "combos" }, { key: "actives" }, { key: "explorer" },
    { key: "science" }, { key: "extras" }, { key: "reviews" }];

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { isPreview, draft } = useProductPreview();
    const { data: saved, isLoading, error } = useQuery({
        queryKey: ["product", slug],
        queryFn: () => fetchProduct(slug, offPreviewRequest()),
        enabled: slug !== PREVIEW_SLUG,
        retry: false,
    });
    // A product whose Live switch is off sends visitors back to the shop.
    React.useEffect(() => {
        if (offReason(error) === "product_off") navigate("/shop", { replace: true });
    }, [error, navigate]);
    const { addItem } = useCart();
    const [adding, runAdd] = useBusyAction();
    const [qty, setQty] = useState(1);
    // In preview mode the CRM streams the unsaved draft in over postMessage.
    const product = isPreview && draft?.product ? { ...(saved || {}), ...draft.product } : saved;
    const form = useFormulation(product);
    const brand = useSeoSettings();
    const { data: reviewData } = useProductReviews(slug);
    const pseo = (product?.website || {}).seo || {};
    usePageSeo(product && !isPreview ? {
        title: pseo.seo_title || `${product.name} — ${product.tagline || "ReHealthMe"}`,
        description: pseo.meta_description || product.tagline
            || (product.description || "").replace(/<[^>]+>/g, " ").slice(0, 155),
        canonical: pseo.canonical_url || `${window.location.origin}/product/${product.slug}`,
        image: (product.images || [])[0] || "",
        jsonLd: [
            productJsonLd(product, brand, reviewData),
            crumbsJsonLd([
                { name: "Home", path: "/" }, { name: "Shop", path: "/shop" },
                { name: product.name, path: `/product/${product.slug}` },
            ]),
        ],
    } : null);
    const { data: savedLayout } = useQuery({
        queryKey: ["product-layout", slug],
        queryFn: async () => (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/product-layout/${slug}`)).json(),
        enabled: slug !== PREVIEW_SLUG,
        retry: false,
    });
    const layout = isPreview && draft?.layout ? draft.layout : savedLayout;

    if (isLoading && !product) {
        return <div className="pt-32 text-center text-zinc-500">Loading…</div>;
    }
    if (!product) {
        return (
            <div className="pt-32 text-center text-zinc-500">
                {isPreview ? "Waiting for the editor…" : "Product not found."}
            </div>
        );
    }

    const addToCart = () => {
        if (product.coming_soon) {
            toast.error("This product is coming soon!");
            return;
        }
        runAdd(() => {
            addItem(product, qty);
            toast.success(`${product.name} added to cart`);
        });
    };

    const benefitPath = product.benefits?.length ? "benefits" : "website.key_benefits";

    return (
        <PreviewCtx.Provider value={{ editing: isPreview }}>
        {product.page_live === false && <OffPreviewBanner label={product.name} />}
        <div className="pt-28 md:pt-32 pb-16">
            <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-12 gap-10">
                <div className="product-media lg:col-span-7 h-[500px] md:h-[640px] lg:h-[720px] lg:sticky lg:top-28 rounded-sm flex items-center justify-center">
                    <div className="product-media__glow pointer-events-none absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full" style={{ background: `radial-gradient(circle, ${(product.color || '#7CB342')}33 0%, rgba(10,10,10,0) 65%)` }} />
                    {product.images?.length ? (
                        <div className="relative w-full h-full p-4 md:p-8">
                            <ProductGallery images={product.images} alt={product.name} />
                        </div>
                    ) : product.shop_type === "ebook" || product.shop_type === "consultation_pack" ? (
                        <div className="relative grid place-items-center gap-4 text-center px-8" data-testid="shop-type-art">
                            {product.shop_type === "ebook"
                                ? <BookOpen className="w-20 h-20 text-gold" />
                                : <Stethoscope className="w-20 h-20 text-lime" />}
                            <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">
                                {product.shop_type === "ebook" ? "DIGITAL GUIDE" : "CONSULTATION PACK"}
                            </div>
                        </div>
                    ) : (
                        <div className="product-media__img relative">
                            <TubeArt
                                name={(product.name.split(" ")[0] || product.name)}
                                suffix={product.name.split(" ").slice(1).join(" ")}
                                color={product.color || "#7CB342"}
                                flavor={(product.flavor || "").toUpperCase()}
                                unitCopy={packCopy(product).unit}
                                count={packCopy(product).count}
                                lines={packCopy(product).lines}
                                className="w-[280px] md:w-[320px] h-auto"
                            />
                        </div>
                    )}
                    {isPreview && (
                        <button type="button" data-testid="pv-edit-images"
                            onClick={() => postEdit({ kind: "product-images" })}
                            className="absolute bottom-3 left-3 z-30 inline-flex items-center gap-2 border border-gold/70
                                bg-black/80 text-gold px-3 py-2 mono text-[9px] tracking-[0.18em] hover:bg-gold hover:text-black">
                            EDIT IMAGES
                        </button>
                    )}
                </div>

                <div className="lg:col-span-5">
                    <Link to="/shop" className="text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-lime" data-testid="back-to-shop-link">← All Products</Link>

                    <motion.h1
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                        className="font-display text-4xl md:text-5xl uppercase tracking-tighter mt-4">
                        <PEdit path="name" value={product.name} label="Product name" />
                    </motion.h1>
                    <PEdit as="div" path="tagline" value={product.tagline} label="Tagline"
                        className="mt-2 text-lime uppercase tracking-[0.2em] text-sm font-semibold" />
                    <RatingLine product={product} className="mt-4" size="w-4 h-4" dim="text-zinc-600"
                        textClass="text-sm text-zinc-400" />

                    <div className="mt-6 flex items-baseline gap-4">
                        {product.price > 0 ? (
                        <span className="font-display text-4xl text-white" data-testid="product-price">{formatINR(product.price)}</span>
                        ) : (
                        <span className="mono text-xs uppercase tracking-[0.25em] text-zinc-500" data-testid="product-price-pending">Pricing announced soon</span>
                        )}
                        {product.compare_at_price && (
                            <span className="text-zinc-500 line-through text-lg">{formatINR(product.compare_at_price)}</span>
                        )}
                        {product.compare_at_price && (
                            <span className="text-xs bg-gold/15 text-gold uppercase tracking-widest px-2 py-1 rounded-full font-bold">
                                Save {Math.round((1 - product.price / product.compare_at_price) * 100)}%
                            </span>
                        )}
                    </div>

                    <PEdit as="p" path="description" value={product.description} multiline
                        label="Description" className="mt-5 text-zinc-400 leading-relaxed" />

                    {!!(product.benefits?.length || product.website?.key_benefits?.length || product.purchasable) && (product.shop_type || "product") === "product" && (
                    <div className="mt-6 grid grid-cols-3 gap-3">
                        {(product.benefits?.length ? product.benefits.slice(0, 3)
                            : product.website?.key_benefits?.length ? product.website.key_benefits.slice(0, 3)
                                : ["No Added Sugar", "No Artificial Colours", "Gentle on Stomach"]).map((b, bi) => (
                            <div key={b} className="flex items-center gap-2 text-xs text-zinc-300 border border-white/10 rounded-sm px-3 py-3">
                                <Check className="w-3.5 h-3.5 text-lime shrink-0" />
                                <PEdit path={`${benefitPath}.${bi}`} value={b} label="Benefit" />
                            </div>
                        ))}
                    </div>
                    )}

                    <div className="mt-8 flex items-center gap-3">
                        {product.purchasable !== false && !product.coming_soon && (
                        <div className="inline-flex items-center gap-3 bg-bg1 rounded-none border border-white/10 px-3 py-2">
                            <button data-testid="pd-qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:text-lime">
                                <Minus className="w-4 h-4" />
                            </button>
                            <span data-testid="pd-qty" className="w-6 text-center font-bold">{qty}</span>
                            <button data-testid="pd-qty-increase" onClick={() => setQty(qty + 1)} className="p-2 hover:text-lime">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        )}
                        <button
                            onClick={addToCart}
                            disabled={product.coming_soon || product.purchasable === false || adding}
                            data-testid="add-to-cart-button"
                            className="btn-pill btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {adding ? <ButtonRing label="Adding…" />
                                : product.coming_soon ? "Coming Soon"
                                    : product.purchasable === false ? "Unavailable" : "Add to Cart"}
                        </button>
                    </div>

                    <TrustStrip className="mt-8" />

                    {(product.shop_type === "consultation_pack" || product.shop_type === "ebook") && (
                        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="shop-type-actions">
                            {product.shop_type === "consultation_pack" && (
                                <Link to={product.consult_area ? `/consultation?area=${product.consult_area}` : "/consultation"}
                                    data-testid="pack-book-appointment"
                                    className="btn-pill border border-lime text-lime hover:bg-lime hover:text-black transition-colors">
                                    Book an appointment
                                </Link>
                            )}
                            {product.shop_type === "ebook" && product.digital_free && product.has_digital_file && (
                                <a href={freeDownloadUrl(product.slug)} data-testid="ebook-free-download"
                                    className="btn-pill btn-primary">Download free guide</a>
                            )}
                            {product.shop_type === "ebook" && !product.digital_free && (
                                <p className="text-xs text-zinc-500">
                                    After checkout the guide appears under <strong>My Downloads</strong> in your account —
                                    stamped with your name, downloadable any time, no outside links.
                                </p>
                            )}
                        </div>
                    )}

                    {(product.coming_soon || product.purchasable === false) && (
                        <div className="mt-6">
                            <NotifyMe productSlug={product.slug} productName={product.name} />
                        </div>
                    )}

                    {(product.shop_type || "product") === "product" ? (
                        <div className="mt-6 grid grid-cols-3 gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                            <div className="flex items-center gap-2"><Truck className="w-3.5 h-3.5 text-lime" /> Free over ₹999</div>
                            <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-lime" /> Quality Assured</div>
                            <div className="flex items-center gap-2"><Leaf className="w-3.5 h-3.5 text-lime" /> Natural</div>
                        </div>
                    ) : (
                        <div className="mt-6 grid grid-cols-2 gap-2 text-[11px] uppercase tracking-widest text-zinc-400">
                            <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-lime" /> No shipping needed</div>
                            <div className="flex items-center gap-2">
                                {product.shop_type === "ebook"
                                    ? <><BookOpen className="w-3.5 h-3.5 text-lime" /> Instant access</>
                                    : <><Stethoscope className="w-3.5 h-3.5 text-lime" /> Qualified practitioner</>}
                            </div>
                        </div>
                    )}

                    <Accordion type="single" collapsible className="mt-10" defaultValue="how">
                        {!form.enabled && !!product.ingredients?.length && (
                            <AccordionItem value="ingredients" className="border-white/10">
                                <AccordionTrigger data-testid="acc-ingredients" className="text-sm uppercase tracking-[0.2em] font-bold">Key Ingredients</AccordionTrigger>
                                <AccordionContent>
                                    <ul className="space-y-3">
                                        {product.ingredients?.map((ing) => (
                                            <li key={ing.name} className="flex gap-3">
                                                <div className="w-1.5 h-1.5 rounded-full bg-lime mt-2 shrink-0" />
                                                <div>
                                                    <div className="text-white font-semibold text-sm">{ing.name}</div>
                                                    <div className="text-zinc-400 text-sm">{ing.benefit}</div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </AccordionContent>
                            </AccordionItem>
                        )}
                        {!!form.usageSteps.length && (
                        <AccordionItem value="how" className="border-white/10">
                            <AccordionTrigger data-testid="acc-how" className="text-sm uppercase tracking-[0.2em] font-bold">
                                {form.sections.usage_title || "How to Enjoy"}
                            </AccordionTrigger>
                            <AccordionContent className="text-zinc-400">
                                <UsageSteps steps={form.usageSteps} note={form.usageNote} criticalWarning={form.criticalWarning} />
                                {form.signatureLine && (
                                    <div data-testid="pd-signature-line">
                                        <PEdit as="div" path="website.signature_line" value={form.signatureLine}
                                            label="Signature line"
                                            className="mt-4 text-gold uppercase tracking-widest text-xs font-bold" />
                                    </div>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                        )}
                        {form.enabled && !!form.scienceText && (
                        <AccordionItem value="science" className="border-white/10">
                            <AccordionTrigger data-testid="acc-science" className="text-sm uppercase tracking-[0.2em] font-bold">The Science</AccordionTrigger>
                            <AccordionContent className="text-zinc-400">
                                <PEdit as="div" path="website.science_text" value={form.scienceText}
                                    multiline label="The Science" />
                            </AccordionContent>
                        </AccordionItem>
                        )}
                        {(form.warnings.length > 0 || form.disclaimer) && (
                            <AccordionItem value="warnings" className="border-white/10">
                                <AccordionTrigger data-testid="acc-warnings" className="text-sm uppercase tracking-[0.2em] font-bold">
                                    {form.sections.warnings_title || "Warnings & Disclaimer"}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <WarningSection warnings={form.warnings} disclaimer={form.disclaimer} criticalWarning={form.criticalWarning} />
                                </AccordionContent>
                            </AccordionItem>
                        )}
                    </Accordion>
                </div>
            </div>

            {/* Order and visibility come from CRM -> Website Layout -> Product Pages */}
            {(layout?.blocks || DEFAULT_BLOCKS).map(({ key }) => {
                if (key === "combos") {
                    return form.enabled ? <ComboSection key={key} sections={form.sections} combos={form.combos}
                        byCombo={form.byCombo} basisLabel={form.basisLabel} /> : null;
                }
                if (key === "actives") {
                    return form.enabled ? <ActivesSection key={key} sections={form.sections} actives={form.actives}
                        comboMap={form.comboMap} trademarks={form.trademarks} /> : null;
                }
                if (key === "explorer") {
                    return form.enabled ? <FormulationExplorer key={key} sections={form.sections} combos={form.combos}
                        byCombo={form.byCombo} comboMap={form.comboMap} ingredients={form.ingredients}
                        basisLabel={form.basisLabel} /> : null;
                }
                if (key === "science") {
                    return <ProductScienceSummary key={key} slug={product.slug} />;
                }
                if (key === "extras") {
                    if (!layout?.extras?.length && !isPreview) return null;
                    return (
                        <div key={key} data-testid="product-extra-sections">
                            <PreviewSections sections={layout?.extras || []} editing={isPreview} />
                        </div>
                    );
                }
                return (
                    <div key={key} className="mx-auto max-w-7xl px-6 md:px-12 mt-12 md:mt-16 space-y-14">
                        <ProductReviews slug={product.slug} />
                        <Testimonials slug={product.slug} limit={6} heading="Customer journeys" />
                    </div>
                );
            })}
        </div>
        </PreviewCtx.Provider>
    );
}
